import { useEffect, useRef, useState } from "react";
import sheetUrl from "../assets/dicta-handdrawn-states-5x2.png";

/**
 * Hand-drawn dictaphone artwork.
 *
 * The device sits still until it is pressed: pointer down pushes the record
 * button (record-press, then record-active while held) and the whole device
 * dips slightly, exactly like pressing a mechanical key. Releasing lets it
 * spring back to idle. No hover animation — the drawing should feel like an
 * object, not a cursor toy.
 */

type SpriteState = "idle" | "record-press" | "record-active";

/** [column, row] within the 5×2 sheet, per dicta-handdrawn-states.json. */
const SHEET_CELLS: Record<SpriteState, readonly [number, number]> = {
  idle: [0, 0],
  "record-press": [3, 1],
  "record-active": [4, 1]
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type DictaLogoProps = {
  interactive?: boolean;
};

export function DictaLogo({ interactive = false }: DictaLogoProps) {
  const [state, setState] = useState<SpriteState>("idle");
  const [retriedSheet, setRetriedSheet] = useState("");
  const holdTimer = useRef<number | null>(null);

  // The artwork is a background image, which fails silently if the fetch
  // drops. Decode it up front and retry once with a cache-bust so the hero
  // never renders an empty rectangle with an orphaned caption.
  useEffect(() => {
    const probe = new Image();
    probe.src = sheetUrl;
    probe.onerror = () => {
      const retry = new Image();
      const busted = `${sheetUrl}${sheetUrl.includes("?") ? "&" : "?"}retry=1`;
      retry.src = busted;
      retry.onload = () => setRetriedSheet(busted);
    };
    return () => {
      probe.onerror = null;
    };
  }, []);

  useEffect(
    () => () => {
      if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    },
    []
  );

  const pressDown = () => {
    setState("record-press");
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    // The red light comes on if the press is held rather than tapped.
    holdTimer.current = window.setTimeout(
      () => setState("record-active"),
      prefersReducedMotion() ? 0 : 140
    );
  };

  const release = () => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    setState("idle");
  };

  const [column, row] = SHEET_CELLS[state];
  return (
    <div
      className={`dicta-logo${interactive ? " dicta-logo--interactive" : ""}${
        state !== "idle" ? " dicta-logo--pressed" : ""
      }`}
      role="img"
      aria-label="Dicta hand-drawn dictaphone"
      onPointerDown={interactive ? pressDown : undefined}
      onPointerUp={interactive ? release : undefined}
      onPointerLeave={interactive ? release : undefined}
      onPointerCancel={interactive ? release : undefined}
      style={{
        backgroundImage: `url(${retriedSheet || sheetUrl})`,
        backgroundPosition: `${column * 25}% ${row * 100}%`
      }}
    />
  );
}
